/**
 * usePotentialLeadsBadge — count of new potential leads since the
 * operator last opened the Potential Leads tab.
 *
 * Two feeds make up the tab, so two feeds make up the badge:
 *  - registered users (`/users/registered`, newest first) whose
 *    `created_at` is newer than the last-seen stamp;
 *  - contacts flagged by an unrepresented open house sign-in
 *    (`/contacts?potential_lead=true`) whose `potential_lead_at` is
 *    newer than the stamp.
 *
 * Pattern lifted from useCommunicationBadge: poll on a slow interval,
 * also refresh on window focus, store the "last seen" timestamp in
 * localStorage so opening the Potential Leads tab clears the badge
 * without needing a server-side acknowledgement endpoint.
 *
 * Both lists are capped at 100 rows, which is more than enough headroom
 * for a "new since last visit" counter — anything past that lands as
 * "99+" anyway. Either feed failing counts as zero for that feed; the
 * badge never surfaces an error.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { usersApi } from '@/features/users/api';
import { contactsApi } from '@/features/contacts/api';
import type { Contact } from '@/types/entities';

const POLL_INTERVAL_MS = 60_000;
export const LEADS_LAST_SEEN_KEY = 'leads.last_seen_at';
const SCAN_PAGE_SIZE = 100;


/** Mark "all current potential leads as seen". Call this when the
 *  operator opens the Potential Leads tab so the badge clears. */
export function markPotentialLeadsSeen(): void {
    try {
        localStorage.setItem(LEADS_LAST_SEEN_KEY, new Date().toISOString());
    } catch {
        /* private mode / quota — non-fatal */
    }
}


function readLastSeenMs(): number {
    try {
        const raw = localStorage.getItem(LEADS_LAST_SEEN_KEY);
        if (!raw) return 0;
        const t = new Date(raw).getTime();
        return Number.isFinite(t) ? t : 0;
    } catch {
        return 0;
    }
}


/** Rows whose timestamp (per `pick`) is newer than `since`. */
function countNewerThan<T>(rows: T[], since: number, pick: (row: T) => string | null | undefined): number {
    return rows.reduce((acc, row) => {
        const raw = pick(row);
        if (!raw) return acc;
        const t = new Date(raw).getTime();
        return Number.isFinite(t) && t > since ? acc + 1 : acc;
    }, 0);
}


async function countNewRegisteredUsers(since: number): Promise<number> {
    try {
        const resp = await usersApi.getRegisteredUsers({ page: 1, per_page: SCAN_PAGE_SIZE });
        return countNewerThan(resp.items || [], since, (u) => (u as { created_at?: string }).created_at);
    } catch {
        return 0;
    }
}


async function countNewOpenHouseLeads(since: number): Promise<number> {
    try {
        const resp = await contactsApi.getContacts({ potential_lead: true, limit: SCAN_PAGE_SIZE });
        return countNewerThan<Contact>(resp.items || [], since, (contact) => contact.potential_lead_at);
    } catch {
        return 0;
    }
}


export function usePotentialLeadsBadge(): number {
    const [count, setCount] = useState(0);
    const inFlightRef = useRef(false);

    const refresh = useCallback(async () => {
        if (inFlightRef.current) return;
        inFlightRef.current = true;
        try {
            const since = readLastSeenMs();
            // First-time visitor (no last_seen yet) sees no badge —
            // otherwise EVERY user and lead looks "new" on first load.
            if (since === 0) {
                setCount(0);
                return;
            }
            const [newUsers, newLeads] = await Promise.all([
                countNewRegisteredUsers(since),
                countNewOpenHouseLeads(since),
            ]);
            setCount(newUsers + newLeads);
        } catch {
            // Auth gone, network blip, whatever — don't pollute the UI.
            // Next interval / focus event will retry.
        } finally {
            inFlightRef.current = false;
        }
    }, []);

    useEffect(() => {
        refresh();
        const id = window.setInterval(refresh, POLL_INTERVAL_MS);
        const onFocus = () => refresh();
        window.addEventListener('focus', onFocus);
        return () => {
            window.clearInterval(id);
            window.removeEventListener('focus', onFocus);
        };
    }, [refresh]);

    return count;
}
